import { Editor, TextElement } from '@tencent/alloy-editor';
import isEqual from 'lodash/isEqual';
import { textElementToTextNode } from '../../../../content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/render/link-text-node';
import type { RectText } from '@tencent/docs-editor';

/**
 * 把相邻且链接信息一致的 textElement 当做同一个链接进行处理
 */
export class SameLinkTextElementsHelper {
    /**
     * 获取和给定 textElement 相邻且链接一致的 textElement 数组
     * @param textElement
     * @param editor
     * @returns
     */
    public static getSameLinkTextElements(textElement: TextElement, editor?: Editor): TextElement[] {
        if (!editor || !textElement) {
            return [];
        }
        return [
            ...this.getLeftSameLinkTextElements(textElement, editor),
            textElement,
            ...this.getRightSameLinkTextElements(textElement, editor),
        ];
    }

    /**
     * 获取一组 textElements 的渲染区域
     * @param textElements
     * @returns
     */
    public static getBounds(textElements: TextElement[]): {
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
    } {
        const textNodes = textElements.reduce(
            (res, textElement) => res.concat(textElementToTextNode.get(textElement)),
            [] as RectText[],
        );
        const initialBounds = {
            minX: Infinity,
            minY: Infinity,
            maxX: 0,
            maxY: 0,
        };
        return textNodes.reduce((bounds, currentNode) => {
            const { minX, minY, maxX, maxY } = bounds;
            const {  minX: nodeMinX, minY: nodeMinY, maxX: nodeMaxX, maxY: nodeMaxY } = currentNode.worldBounds;
            return {
                minX: Math.min(minX, nodeMinX),
                minY: Math.min(minY, nodeMinY),
                maxX: Math.max(maxX, nodeMaxX),
                maxY: Math.max(maxY, nodeMaxY),
            };
        }, initialBounds);
    }

    /**
     * 获取和给定 textElement 【左侧】相邻且链接一致的 textElement 数组
     * @param textElement
     * @param editor
     * @returns
     */
    private static getLeftSameLinkTextElements(textElement: TextElement, editor: Editor): TextElement[] {
        const prevTextElement = editor.getPrevTextElement(textElement);
        if (!prevTextElement) {
            return [];
        }
        const currentLinkConfig = this.getLinkConfig(textElement);
        const prevLinkConfig = this.getLinkConfig(prevTextElement);
        if (this.isSameLink(prevLinkConfig, currentLinkConfig)) {
            return [
                ...this.getLeftSameLinkTextElements(prevTextElement, editor),
                prevTextElement,
            ];
        }
        return [];
    }

    /**
     * 获取和给定 textElement 【右侧】相邻且链接一致的 textElement 数组
     * @param textElement
     * @param editor
     * @returns
     */
    private static getRightSameLinkTextElements(textElement: TextElement, editor: Editor): TextElement[] {
        const nextTextElement = editor.getNextTextElement(textElement);
        if (!nextTextElement) {
            return [];
        }
        const currentLinkConfig = this.getLinkConfig(textElement);
        const nextLinkConfig = this.getLinkConfig(nextTextElement);
        if (this.isSameLink(nextLinkConfig, currentLinkConfig)) {
            return [
                nextTextElement,
                ...this.getRightSameLinkTextElements(nextTextElement, editor),
            ];
        }
        return [];
    }

    /**
     * 判断给定的两个链接配置是否相同
     * @param link
     * @param otherLink
     * @returns
     */
    private static isSameLink(link: any, otherLink: any): boolean {
        if (link === otherLink) {
            return true;
        }
        if (!link || !otherLink) {
            return false;
        }
        return link.url === otherLink.url
       && link.tooltip === otherLink.tooltip
        && isEqual(link.slideJump, otherLink.slideJump);
    }

    private static getLinkConfig(textElement?: TextElement): any {
        return textElement?.['origin-href'] || null;
    }
}
